import { type ReactNode } from 'react'
import styles from './AddInventoryItemsToEditDialog.module.css'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { SearchField } from '@/components/SearchField'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { Button, RawButton } from '@/components/Button'

export interface AddInventoryItemsToEditDialogProps {
  showing: boolean
  onClose: () => unknown
  searchText: string
  onSearchTextChange: (text: string) => unknown
  selectedCount: number
  onAddItems: () => unknown
  /**
   * The infinite scroll loader, rendered below the items.
   */
  loader?: ReactNode
  /**
   * An `InventoryItemListItem` per service item.
   */
  children?: ReactNode
}

export function AddInventoryItemsToEditDialog({
  showing,
  onClose,
  searchText,
  onSearchTextChange,
  selectedCount,
  onAddItems,
  loader,
  children,
}: AddInventoryItemsToEditDialogProps) {
  return (
    <ModalSheet showing={showing} onRequestClose={onClose}>
      <ModalSheetHeaderShell size="auto">
        <div className={styles.header}>
          <div className={styles.closeButtonContainer}>
            <ModalSheetTitle>Add service items</ModalSheetTitle>
            <ModalSheetHeaderCloseButton />
          </div>
          <div className={styles.searchContainer}>
            <SearchField
              autoFocus
              stretch
              className={styles.scopeSearch}
              placeholder="Type to search service items"
              onChange={onSearchTextChange}
              value={searchText}
              testId="search-field"
            />
          </div>
        </div>
      </ModalSheetHeaderShell>

      <ModalSheetContent>
        <div className={styles.itemsContainer}>
          {children}
          {loader}
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" onPress={onClose} variant="default" />
          <Button
            label={`Assign ${selectedCount}`}
            onPress={onAddItems}
            variant="primary"
            testId="scope-change-request-edit"
            isDisabled={selectedCount <= 0}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface InventoryItemListItemProps {
  name: string
  description: string
  selected: boolean
  onToggleSelected: () => unknown
}

export function InventoryItemListItem({
  name,
  description,
  selected,
  onToggleSelected,
}: InventoryItemListItemProps) {
  return (
    <RawButton
      className={cx(styles.itemContainer, selected && styles.selected)}
      aria-pressed={selected}
      onPress={onToggleSelected}
      testId={`scope-change-inventory-item-${name}`}
    >
      <div className={styles.checkboxContainer}>
        <Icon
          color="var(--theme-base)"
          name={selected ? 'checkbox' : 'checkbox-blank'}
          size={24}
        />
      </div>

      <div className={styles.textContainer}>
        <div className={styles.title}>{name}</div>
        <div className={styles.description}>{description}</div>
      </div>
    </RawButton>
  )
}
